import {
  isFlowRetired,
  retireFlow,
  unretireFlow,
} from 'helpers/flowReentryGate';
import { acquireReloadGate, releaseReloadGate } from 'helpers/reloadGate';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';

// Module-scope Back/Forward guard.
//
// The listener deliberately lives at module scope rather than inside the hook.
// React Router adds its own popstate listener from BrowserRouter's *layout* effect,
// so a page-owned listener (a passive effect) is always registered after it. React
// treats popstate as a discrete event and flushes passive effects synchronously at
// the end of that commit, which means a page-owned listener is removed by its own
// cleanup during the very popstate dispatch it exists to intercept, and the browser
// never invokes it. That is why the previous in-page implementations could not hold
// a back press that changed the matched route.
//
// Registering here instead runs before React Router's listener, so the guarded URL is
// restored before React Router reads window.location. React Router therefore never
// observes the route change, never re-renders to the popped route, and never unmounts
// the guarded page. The listener is a strict no-op whenever no page is armed.

const LISTENER_KEY = '__preventBrowserBackButtonPopStateListener';

let guardedHref = null;

const handleGlobalPopState = () => {
  if (!guardedHref) return;

  // Carry the current entry's state forward rather than writing null, so we do not
  // gratuitously clobber react-router's bookkeeping (`idx`/`key`) when it happens to
  // be present. It often is not: domUtils.replaceUrlWithoutReload writes a null state
  // and changeAddressBarUrlWithoutReloadPage writes `{path}`, neither of which carries
  // an `idx`. Nothing here depends on that state being well-formed -- this app uses
  // <BrowserRouter>, which ignores the `delta` derived from it.
  try {
    window.history.pushState(window.history.state, '', guardedHref);
  } catch (error) {
    // iOS caps pushState calls. Swallowing here matters: an exception escaping this
    // listener would let react-router's handler run against the popped location and
    // unmount the page, which is exactly what the guard exists to prevent.
  }
};

if (typeof window !== 'undefined') {
  // Drop a listener left behind by a previous evaluation of this module. Only
  // react-refresh re-evaluates it, and in that case ours is registered after
  // react-router's and cannot regain first position -- reload the dev session if the
  // guard misbehaves after an edit. In a production bundle this module is evaluated
  // exactly once, while src/router/index.js resolves its page imports, so the
  // listener is always registered before BrowserRouter mounts.
  if (window[LISTENER_KEY]) {
    window.removeEventListener('popstate', window[LISTENER_KEY]);
  }
  window[LISTENER_KEY] = handleGlobalPopState;
  window.addEventListener('popstate', handleGlobalPopState);
}

// `reentryFallback` -- where to send the user if they Back their way into this flow
// after leaving it. Optional: omit it and the hook behaves exactly as it did before,
// guard only, no re-entry check.
//
// It is passed per call site rather than resolved from a pathname -> home map kept
// inside this hook. helpers/reloadGate.js:5-9 records what goes wrong with that shape:
// useBuildDetector gated on `pathsTriggerReloadLater`, a two-entry pathname allowlist
// standing in for "a guarded flow is active", and it diverged from the thing it
// represented -- six consumers, two of them named. A pathname -> home map here would be
// the same proxy with the same failure mode: a seventh consumer, or a route rename, and
// the map silently stops covering it with no error anywhere. The page that owns the flow
// already knows its own exit destination; it can just say so.
const usePreventBrowserBackButton = (reentryFallback) => {
  const guardedPathnameRef = useRef(null);
  const navigate = useNavigate();

  // Sampled at render, i.e. for this mount. 'POP' means the browser moved through
  // history (Back/Forward) rather than the app calling navigate()/push.
  const navigationType = useNavigationType();

  // Set when this mount is a rejected re-entry, so the mount effect below does not arm
  // the guard for a page we are already redirecting away from.
  const isRejectedReentryRef = useRef(false);

  // Resample on every render rather than capturing once at mount, so query-string
  // rewrites (domUtils.changeAddressBarUrlWithoutReloadPage) are tracked. Only adopt
  // the href while the *pathname* still matches the one this page mounted on:
  // domUtils.replaceUrlWithoutReload can point the address bar at a different route
  // (DiscountTicket closes its details modal by replacing the URL with /offers) while
  // this page is still mounted, and restoring that href would make react-router
  // navigate there -- the exact unmount this guard exists to prevent.
  useEffect(() => {
    // A rejected re-entry must not adopt an href. This effect runs after the layout
    // effect below on the same commit, so the flag is already set. Skipping matters
    // twice over: arming the module-scope listener for a page the user is being
    // redirected away from would be wrong on its own, and because the mount effect
    // below returns early on a rejected re-entry there is no cleanup to null
    // `guardedHref` afterwards -- it would stay set for the rest of the session and
    // hold every subsequent back press on the URL of a flow nobody is in.
    if (isRejectedReentryRef.current) return;

    if (guardedPathnameRef.current === null) {
      guardedPathnameRef.current = window.location.pathname;
    }

    if (window.location.pathname === guardedPathnameRef.current) {
      guardedHref = `${window.location.pathname}${window.location.search}`;
    }
  });

  // A layout effect, not a passive one, and ordered before the arming effect below so
  // its decision is already made by the time that effect runs. React fires layout
  // effects synchronously after commit and before paint, so the redirect is issued
  // before the browser paints the guarded page.
  //
  // Honest limitation, stated rather than papered over: this does NOT mean the flow
  // never renders. The component renders once -- its render function runs, its children
  // mount -- and only then does this effect run and redirect. Anything the flow does
  // during that render still happens. What this buys is that the user does not *see* the
  // flow, and that the guard is never armed for it. If a flow must not even execute its
  // render on re-entry, that needs a route-level check, not a hook.
  useLayoutEffect(() => {
    const pathname = window.location.pathname;

    // Why 'POP' is the right predicate here, and why the registry must stay in memory.
    //
    // useNavigationType() returns 'POP' for a Back or Forward press -- the case we want
    // to reject -- but it also returns 'POP' on the very first render of a fresh
    // document, where nothing was popped at all. That second case would be a serious
    // false positive except for one fact: helpers/flowReentryGate.js keeps its registry
    // in module memory only, so a fresh document always starts with an empty registry
    // and isFlowRetired() is false. The two conditions together are what make this safe,
    // and this is precisely why that registry must never be moved to sessionStorage --
    // the payment return (/tran?paymentStatus=SUCCESS) is a fresh-document 'POP' arrival
    // into a flow the user genuinely exited before paying.
    const isRejectedReentry =
      !!reentryFallback && navigationType === 'POP' && isFlowRetired(pathname);

    isRejectedReentryRef.current = isRejectedReentry;

    if (isRejectedReentry) {
      // Keep the retirement in place. A flow occupies several history entries -- the
      // guard's own sentinel at minimum, plus whatever the flow itself pushed -- so one
      // bounce is not the end of it: the next Back press lands on the next flow entry
      // down and has to be bounced too. Clearing here would disarm the gate after a
      // single press and let the second one through, which is exactly the multi-entry
      // case this whole design exists to handle.
      //
      // Nothing latches as a result. A deliberate return to the flow arrives as a PUSH,
      // and the branch below clears the retirement then. And because the redirect is a
      // replace, each bounce consumes the flow entry it landed on, so the leftover
      // entries drain rather than bouncing forever.
      navigate(reentryFallback, { replace: true });

      return;
    }

    // A legitimate arrival re-arms the flow -- entering it again on purpose must work
    // normally.
    unretireFlow(pathname);
    // Mount-only: this is an arrival check, not something to re-run on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Capture the pathname here, in the effect body, rather than reading
    // guardedPathnameRef.current inside the cleanup. That ref is nulled by this very
    // cleanup, and in a mount/unmount race it can already be null when the cleanup runs
    // -- retireFlow(null) is ignored, so the flow would silently never be retired and
    // the whole gate would do nothing, with no error to show for it.
    const guardedPathname = window.location.pathname;

    if (isRejectedReentryRef.current) {
      // Rejected re-entry: do not arm anything. No reload gate, no sentinel entry, and
      // no retirement on the way out -- this mount is being redirected away from, not
      // exited by the user.
      return undefined;
    }

    acquireReloadGate();

    // Guarantee an in-app entry to land on, so a back press fires popstate even when
    // the guarded page is the first entry of the session.
    try {
      window.history.pushState(window.history.state, '', window.location.href);
    } catch (error) {
      // Losing this sentinel entry only means a back press from the session's first
      // entry leaves the app, which is the pre-existing behaviour.
    }

    return () => {
      guardedHref = null;
      guardedPathnameRef.current = null;
      releaseReloadGate();

      // The flow is being left. Record it, so a later Back press into this pathname is
      // recognised as a re-entry rather than a fresh arrival.
      retireFlow(guardedPathname);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default usePreventBrowserBackButton;
